import React, { useState, type ReactNode, useMemo } from 'react';
import { styled } from '@compiled/react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { notificationTabsQuery } from '@atlassian/jira-relay/src/__generated__/notificationTabsQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { messages } from './messages';
import { NotificationLogsTable } from './notification-table';

export const Panel = ({ children, testId }: { children: ReactNode; testId?: string }) => (
	<PanelWrapper data-testid={testId}>{children}</PanelWrapper>
);

export const QUERY = graphql`
	query notificationTabsQuery(
		$cloudId: ID!
		$projectId: Long
		$notificationType: String
		$notificationActionType: String
	) {
		...notificationTable_spaAppsServicedeskNotificationLogs_NotificationLogsTable
	}
`;

export const NotificationTabs = ({
	projectContext,
	outgoingMailConfigData,
}: {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	projectContext: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	outgoingMailConfigData: any;
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const tabTitles = ['customerInvite', 'issueFailure'];
	const [selectedTab, setSelectedTab] = useState('CUSTOMER_INVITE');

	const cloudId = useCloudId();
	const projectId = projectContext?.projectId;
	const projectKey = projectContext?.projectKey;

	// // Create a unique fetchKey for each selectedTab
	const fetchKey = useMemo(() => selectedTab, [selectedTab]);

	// Use useLazyLoadQuery to fetch data
	const emailData = useLazyLoadQuery<notificationTabsQuery>(
		QUERY,
		{
			cloudId,
			projectId,
			notificationType: selectedTab === 'CUSTOMER_INVITE' ? null : 'CUSTOMER_NOTIFICATION.ISSUE',
			notificationActionType: selectedTab === 'CUSTOMER_INVITE' ? 'CUSTOMER_INVITE' : null,
		},
		{ fetchKey, fetchPolicy: 'network-only' },
	);

	// Handle tab change
	const handleTabChange = (index: number) => {
		if (index === 0) {
			const analyticsEventForCustomerInvite = createAnalyticsEvent({
				actionSubject: 'customerInviteNotificationTabs',
				action: 'clicked',
			});
			fireUIAnalytics(analyticsEventForCustomerInvite);
			setSelectedTab('CUSTOMER_INVITE');
		}
		if (index === 1) {
			const analyticsEventForIssueNotification = createAnalyticsEvent({
				actionSubject: 'issueNotificationNotificationTabs',
				action: 'clicked',
			});
			fireUIAnalytics(analyticsEventForIssueNotification);
			setSelectedTab('ISSUE_NOTIFICATION');
		}
	};

	return (
		<Tabs onChange={handleTabChange} id="default">
			<TabList>
				{tabTitles.map((title, index) => {
					if (title === 'customerInvite') {
						return (
							<Tab
								key={index}
								testId="spa-apps-servicedesk-notification-logs.ui.notification-logs-page.notification-logs-tab-layout.notification-tabs.tab-1"
							>
								{formatMessage(messages.customerInvite)}{' '}
							</Tab>
						);
					}
					return (
						<Tab
							key={index}
							testId="spa-apps-servicedesk-notification-logs.ui.notification-logs-page.notification-logs-tab-layout.notification-tabs.tab-2"
						>
							{formatMessage(messages.issueFailure)}
						</Tab>
					);
				})}
			</TabList>
			{tabTitles.map((_, index) => (
				<TabPanel key={index}>
					<Panel
						testId={`spa-apps-servicedesk-notification-logs.ui.notification-logs-page.notification-logs-tab-layout.notification-tabs.panel-${
							index + 1
						}`}
					>
						<NotificationLogsTable
							emailData={emailData}
							projectKey={projectKey}
							outgoingMailConfigData={outgoingMailConfigData}
							selectedTab={selectedTab}
						/>
					</Panel>
				</TabPanel>
			))}
		</Tabs>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PanelWrapper = styled.div({
	width: '100%',
});
